import { supabase } from '../supabase';
import type {
  BinInventory,
  BinInventoryWithProduct,
  BinLocationAddress,
  ProductBinLocation,
  BinContentsItem,
  BinAssignmentData,
  BinTransferData,
  BinMovementHistoryWithDetails,
  WarehouseBinWithInventory,
  BinPickSuggestion,
  BinProductPreview,
} from '../../types/warehouse';

export async function assignProductToBin(
  data: BinAssignmentData,
  adminId: string,
  userId: string
): Promise<{ success: boolean; error?: string; data?: BinInventory }> {
  try {
    const { data: existing } = await supabase
      .from('bin_inventory')
      .select('id, bin_id')
      .eq('product_id', data.product_id)
      .maybeSingle();

    if (existing) {
      return {
        success: false,
        error: 'This product is already assigned to a bin. Please transfer it instead.',
      };
    }

    const { data: inserted, error } = await supabase
      .from('bin_inventory')
      .insert({
        bin_id: data.bin_id,
        product_id: data.product_id,
        warehouse_id: data.warehouse_id,
        admin_id: adminId,
        quantity: data.quantity,
        assigned_by: userId,
        notes: data.notes || null,
      })
      .select()
      .single();

    if (error) {
      console.error('Error assigning product to bin:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: inserted };
  } catch (err) {
    console.error('Error in assignProductToBin:', err);
    return { success: false, error: 'Failed to assign product to bin' };
  }
}

export async function updateBinInventoryQuantity(
  binInventoryId: string,
  quantity: number,
  userId: string,
  notes?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('bin_inventory')
      .update({
        quantity,
        assigned_by: userId,
        notes: notes || null,
      })
      .eq('id', binInventoryId);

    if (error) {
      console.error('Error updating bin inventory:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    console.error('Error in updateBinInventoryQuantity:', err);
    return { success: false, error: 'Failed to update quantity' };
  }
}

export async function removeProductFromBin(
  binInventoryId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('bin_inventory')
      .delete()
      .eq('id', binInventoryId);

    if (error) {
      console.error('Error removing product from bin:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    console.error('Error in removeProductFromBin:', err);
    return { success: false, error: 'Failed to remove product from bin' };
  }
}

export async function transferProductBetweenBins(
  data: BinTransferData,
  userId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('bin_inventory')
      .update({
        bin_id: data.to_bin_id,
        warehouse_id: data.warehouse_id,
        quantity: data.quantity,
        assigned_by: userId,
        notes: data.reason || null,
      })
      .eq('product_id', data.product_id)
      .eq('bin_id', data.from_bin_id);

    if (error) {
      console.error('Error transferring product:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    console.error('Error in transferProductBetweenBins:', err);
    return { success: false, error: 'Failed to transfer product' };
  }
}

export async function getBinContents(binId: string, adminId: string): Promise<BinContentsItem[]> {
  if (!adminId) return [];
  try {
    const { data, error } = await supabase.rpc('get_bin_contents', {
      p_bin_id: binId,
      p_admin_id: adminId,
    });

    if (error) {
      console.error('Error fetching bin contents:', error);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('Error in getBinContents:', err);
    return [];
  }
}

export async function getBinLocationAddress(binId: string): Promise<BinLocationAddress | null> {
  try {
    const { data, error } = await supabase.rpc('get_bin_location_address', {
      p_bin_id: binId,
    });

    if (error || !data || data.length === 0) {
      console.error('Error fetching bin location:', error);
      return null;
    }

    return data[0];
  } catch (err) {
    console.error('Error in getBinLocationAddress:', err);
    return null;
  }
}

export async function getProductBinLocation(
  productId: string,
  adminId: string,
): Promise<ProductBinLocation | null> {
  if (!adminId) return null;
  try {
    const { data, error } = await supabase.rpc('get_product_bin_location', {
      p_product_id: productId,
      p_admin_id: adminId,
    });

    if (error || !data || data.length === 0) {
      return null;
    }

    return data[0];
  } catch (err) {
    console.error('Error in getProductBinLocation:', err);
    return null;
  }
}

export async function getProductsBinLocations(
  productIds: string[],
  adminId?: string
): Promise<Map<string, ProductBinLocation>> {
  const result = new Map<string, ProductBinLocation>();
  if (productIds.length === 0) return result;

  if (adminId) {
    try {
      const { data, error } = await supabase.rpc('get_product_bin_locations', {
        p_product_ids: productIds,
        p_admin_id: adminId,
      });

      if (error) {
        console.error('RPC get_product_bin_locations error:', error);
      } else if (data && Array.isArray(data)) {
        for (const row of data as any[]) {
          result.set(row.product_id, {
            bin_inventory_id: row.bin_inventory_id,
            quantity: row.quantity,
            warehouse_id: row.warehouse_id,
            warehouse_name: row.warehouse_name,
            zone_id: row.zone_id || null,
            zone_code: row.zone_code || null,
            rack_id: row.rack_id,
            rack_code: row.rack_code,
            bin_id: row.bin_id,
            bin_code: row.bin_code,
            full_address: row.full_address,
            short_code: row.short_code,
          });
        }
        return result;
      }
    } catch (err) {
      console.error('RPC get_product_bin_locations exception:', err);
    }
  }

  const BATCH_SIZE = 100;
  const batches: string[][] = [];
  for (let i = 0; i < productIds.length; i += BATCH_SIZE) {
    batches.push(productIds.slice(i, i + BATCH_SIZE));
  }

  const fetchBatch = async (batchIds: string[], retryCount = 0): Promise<any[]> => {
    try {
      const query = supabase
        .from('bin_inventory')
        .select(`
          id,
          product_id,
          quantity,
          bin:warehouse_bins!inner(
            id,
            bin_code,
            rack:warehouse_racks!inner(
              id,
              rack_code,
              zone:warehouse_zones(
                id,
                zone_code
              ),
              warehouse:warehouses!inner(
                id,
                name
              )
            )
          )
        `)
        .in('product_id', batchIds);

      if (adminId) {
        query.eq('admin_id', adminId);
      }

      const { data, error } = await query;

      if (error) {
        if (retryCount < 3) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
          return fetchBatch(batchIds, retryCount + 1);
        }
        console.error('Error fetching bin locations batch:', error);
        return [];
      }

      return data || [];
    } catch (err) {
      if (retryCount < 3) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return fetchBatch(batchIds, retryCount + 1);
      }
      console.error('Error in fetchBatch for bin locations:', err);
      return [];
    }
  };

  try {
    const batchResults = await Promise.all(batches.map(batch => fetchBatch(batch)));

    for (const batchData of batchResults) {
      for (const item of batchData) {
        const bin = item.bin as any;
        const rack = bin?.rack;
        const zone = rack?.zone;
        const warehouse = rack?.warehouse;

        if (bin && rack && warehouse) {
          const zoneCode = zone?.zone_code || null;
          const fullAddress = zoneCode
            ? `${warehouse.name} > ${zoneCode} > ${rack.rack_code} > ${bin.bin_code}`
            : `${warehouse.name} > ${rack.rack_code} > ${bin.bin_code}`;

          const shortCode = zoneCode
            ? `${warehouse.name.substring(0, 3).toUpperCase()}-${zoneCode}-${rack.rack_code}-${bin.bin_code}`
            : `${warehouse.name.substring(0, 3).toUpperCase()}-${rack.rack_code}-${bin.bin_code}`;

          result.set(item.product_id, {
            bin_inventory_id: item.id,
            quantity: item.quantity,
            warehouse_id: warehouse.id,
            warehouse_name: warehouse.name,
            zone_id: zone?.id || null,
            zone_code: zoneCode,
            rack_id: rack.id,
            rack_code: rack.rack_code,
            bin_id: bin.id,
            bin_code: bin.bin_code,
            full_address: fullAddress,
            short_code: shortCode,
          });
        }
      }
    }

    return result;
  } catch (err) {
    console.error('Error in getProductsBinLocations:', err);
    return result;
  }
}

export async function getBinInventoryForWarehouse(
  warehouseId: string,
  adminId: string
): Promise<BinInventoryWithProduct[]> {
  try {
    const { data, error } = await supabase
      .from('bin_inventory')
      .select(`
        *,
        product:products!inner(
          id,
          name,
          sku,
          barcode,
          category,
          quantity,
          image_url
        ),
        assigned_by_user:users!bin_inventory_assigned_by_fkey(
          id,
          full_name
        )
      `)
      .eq('warehouse_id', warehouseId)
      .eq('admin_id', adminId)
      .order('assigned_at', { ascending: false });

    if (error) {
      console.error('Error fetching warehouse bin inventory:', error);
      return [];
    }

    return (data || []) as BinInventoryWithProduct[];
  } catch (err) {
    console.error('Error in getBinInventoryForWarehouse:', err);
    return [];
  }
}

export async function getBinsWithInventoryCounts(
  rackId: string,
  _adminId?: string
): Promise<WarehouseBinWithInventory[]> {
  try {
    const { data: bins, error: binsError } = await supabase
      .from('warehouse_bins')
      .select('*')
      .eq('rack_id', rackId)
      .order('sort_order');

    if (binsError) {
      console.error('Error fetching bins:', binsError);
      return [];
    }

    const binIds = (bins || []).map((b) => b.id);
    if (binIds.length === 0) return [];

    const { data: inventoryData, error: countError } = await supabase
      .from('bin_inventory')
      .select('bin_id, product_id, products(name, image_url)')
      .in('bin_id', binIds);

    if (countError) {
      console.error('Error fetching inventory counts:', countError);
    }

    const countMap = new Map<string, number>();
    const previewMap = new Map<string, BinProductPreview[]>();

    for (const item of inventoryData || []) {
      countMap.set(item.bin_id, (countMap.get(item.bin_id) || 0) + 1);

      const product = item.products as unknown as { name: string; image_url: string | null } | null;
      if (product) {
        const previews = previewMap.get(item.bin_id) || [];
        if (previews.length < 4) {
          previews.push({
            product_id: item.product_id,
            name: product.name,
            image_url: product.image_url || null,
          });
          previewMap.set(item.bin_id, previews);
        }
      }
    }

    return (bins || []).map((bin) => ({
      ...bin,
      inventory_count: countMap.get(bin.id) || 0,
      preview_items: previewMap.get(bin.id) || [],
    }));
  } catch (err) {
    console.error('Error in getBinsWithInventoryCounts:', err);
    return [];
  }
}

export async function getUnassignedProducts(adminId: string): Promise<{
  id: string;
  name: string;
  sku: string;
  barcode: string | null;
  category: string;
  quantity: number;
  image_url: string | null;
}[]> {
  try {
    const { data: allProducts, error: prodError } = await supabase
      .from('products')
      .select('id, name, sku, barcode, category, quantity, image_url')
      .eq('admin_id', adminId)
      .eq('status', 'active')
      .order('name');

    if (prodError) {
      console.error('Error fetching products:', prodError);
      return [];
    }

    const { data: assignedProducts, error: assignedError } = await supabase
      .from('bin_inventory')
      .select('product_id')
      .eq('admin_id', adminId);

    if (assignedError) {
      console.error('Error fetching assigned products:', assignedError);
      return allProducts || [];
    }

    const assignedIds = new Set((assignedProducts || []).map((p) => p.product_id));
    return (allProducts || []).filter((p) => !assignedIds.has(p.id));
  } catch (err) {
    console.error('Error in getUnassignedProducts:', err);
    return [];
  }
}

export async function searchUnassignedProducts(
  adminId: string,
  searchTerm: string
): Promise<{
  id: string;
  name: string;
  sku: string;
  barcode: string | null;
  category: string;
  quantity: number;
  image_url: string | null;
}[]> {
  if (!searchTerm.trim()) {
    return getUnassignedProducts(adminId);
  }

  try {
    const term = searchTerm.toLowerCase().trim();
    const { data: products, error } = await supabase
      .rpc('search_products_simple', {
        p_admin_id: adminId,
        p_search_term: term,
        p_limit: 50
      });

    if (error) {
      console.error('Error searching products:', error);
      return [];
    }

    const activeProducts = (products || []).filter((p: any) => p.status === 'active');

    const { data: assignedProducts } = await supabase
      .from('bin_inventory')
      .select('product_id')
      .eq('admin_id', adminId);

    const assignedIds = new Set((assignedProducts || []).map((p) => p.product_id));
    return activeProducts.filter((p: any) => !assignedIds.has(p.id));
  } catch (err) {
    console.error('Error in searchUnassignedProducts:', err);
    return [];
  }
}

export interface ProductWithBinInfo {
  id: string;
  name: string;
  sku: string;
  barcode: string | null;
  category: string;
  quantity: number;
  image_url: string | null;
  current_bin: {
    bin_id: string;
    bin_code: string;
    bin_inventory_id: string;
    assigned_quantity: number;
  } | null;
}

export async function searchAllProductsWithBinInfo(
  adminId: string,
  searchTerm: string,
  excludeBinId?: string
): Promise<ProductWithBinInfo[]> {
  try {
    const term = searchTerm.toLowerCase().trim();
    let products: any[] = [];

    if (term) {
      const { data: searchResults, error: searchError } = await supabase
        .rpc('search_products_simple', {
          p_admin_id: adminId,
          p_search_term: term,
          p_limit: 50
        });

      if (searchError) {
        console.error('Error searching products:', searchError);
        return [];
      }
      products = (searchResults || []).filter((p: any) => p.status === 'active');
    } else {
      const { data: allProducts, error: productError } = await supabase
        .from('products')
        .select('id, name, sku, barcode, category, quantity, image_url')
        .eq('admin_id', adminId)
        .eq('status', 'active')
        .order('name')
        .limit(50);

      if (productError) {
        console.error('Error fetching products:', productError);
        return [];
      }
      products = allProducts || [];
    }

    if (products.length === 0) {
      return [];
    }

    const productIds = products.map((p) => p.id);
    const { data: binInventory, error: binError } = await supabase
      .from('bin_inventory')
      .select(`
        id,
        product_id,
        bin_id,
        quantity,
        bin:warehouse_bins!inner(id, bin_code)
      `)
      .eq('admin_id', adminId)
      .in('product_id', productIds);

    if (binError) {
      console.error('Error fetching bin inventory:', binError);
    }

    const binMap = new Map<string, {
      bin_id: string;
      bin_code: string;
      bin_inventory_id: string;
      assigned_quantity: number;
    }>();

    for (const item of binInventory || []) {
      const bin = item.bin as unknown as { id: string; bin_code: string };
      if (bin && bin.id && bin.bin_code) {
        binMap.set(item.product_id, {
          bin_id: bin.id,
          bin_code: bin.bin_code,
          bin_inventory_id: item.id,
          assigned_quantity: item.quantity,
        });
      }
    }

    const results: ProductWithBinInfo[] = products.map((product) => ({
      ...product,
      current_bin: binMap.get(product.id) || null,
    }));

    if (excludeBinId) {
      return results.filter(
        (p) => !p.current_bin || p.current_bin.bin_id !== excludeBinId
      );
    }

    return results;
  } catch (err) {
    console.error('Error in searchAllProductsWithBinInfo:', err);
    return [];
  }
}

export async function getBinMovementHistory(
  adminId: string,
  options?: {
    productId?: string;
    binId?: string;
    warehouseId?: string;
    limit?: number;
  }
): Promise<BinMovementHistoryWithDetails[]> {
  try {
    let query = supabase
      .from('bin_movement_history')
      .select(`
        *,
        product:products(id, name, sku),
        from_bin:warehouse_bins!bin_movement_history_from_bin_id_fkey(id, bin_code),
        to_bin:warehouse_bins!bin_movement_history_to_bin_id_fkey(id, bin_code),
        performed_by_user:users!bin_movement_history_performed_by_fkey(id, full_name)
      `)
      .eq('admin_id', adminId)
      .order('created_at', { ascending: false });

    if (options?.productId) {
      query = query.eq('product_id', options.productId);
    }
    if (options?.binId) {
      query = query.or(`from_bin_id.eq.${options.binId},to_bin_id.eq.${options.binId}`);
    }
    if (options?.warehouseId) {
      query = query.eq('warehouse_id', options.warehouseId);
    }
    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching movement history:', error);
      return [];
    }

    return (data || []) as BinMovementHistoryWithDetails[];
  } catch (err) {
    console.error('Error in getBinMovementHistory:', err);
    return [];
  }
}

export async function generatePickList(
  items: { product_id: string; quantity: number }[],
  adminId: string
): Promise<BinPickSuggestion[]> {
  const productIds = items.map((i) => i.product_id);
  const quantityMap = new Map(items.map((i) => [i.product_id, i.quantity]));

  const locations = await getProductsBinLocations(productIds, adminId);

  const { data: products } = await supabase
    .from('products')
    .select('id, name, sku')
    .in('id', productIds);

  const productMap = new Map((products || []).map((p) => [p.id, p]));

  const suggestions: BinPickSuggestion[] = [];

  for (const [productId, location] of locations) {
    const product = productMap.get(productId);
    if (!product) continue;

    suggestions.push({
      product_id: productId,
      product_name: product.name,
      product_sku: product.sku,
      requested_quantity: quantityMap.get(productId) || 0,
      bin_location: location,
      available_quantity: location.quantity,
      pick_order: 0,
    });
  }

  for (const item of items) {
    if (!locations.has(item.product_id)) {
      const product = productMap.get(item.product_id);
      if (product) {
        suggestions.push({
          product_id: item.product_id,
          product_name: product.name,
          product_sku: product.sku,
          requested_quantity: item.quantity,
          bin_location: {
            warehouse_id: '',
            warehouse_name: 'Unassigned',
            zone_id: null,
            zone_code: null,
            rack_id: '',
            rack_code: '',
            bin_id: '',
            bin_code: 'N/A',
            full_address: 'No bin assigned',
            short_code: 'N/A',
          },
          available_quantity: 0,
          pick_order: 999,
        });
      }
    }
  }

  suggestions.sort((a, b) => {
    if (a.bin_location.warehouse_name !== b.bin_location.warehouse_name) {
      return a.bin_location.warehouse_name.localeCompare(b.bin_location.warehouse_name);
    }
    if ((a.bin_location.zone_code || '') !== (b.bin_location.zone_code || '')) {
      return (a.bin_location.zone_code || '').localeCompare(b.bin_location.zone_code || '');
    }
    if (a.bin_location.rack_code !== b.bin_location.rack_code) {
      return a.bin_location.rack_code.localeCompare(b.bin_location.rack_code);
    }
    return a.bin_location.bin_code.localeCompare(b.bin_location.bin_code);
  });

  return suggestions.map((s, idx) => ({ ...s, pick_order: idx + 1 }));
}

export interface WorkerProductInfo {
  id: string;
  barcode: string | null;
  image_url: string | null;
  color_name: string | null;
  size_name: string | null;
}

export interface WorkerLocationMap {
  productMap: Map<string, WorkerProductInfo>;
  productMapByNorm: Map<string, WorkerProductInfo>;
  locationMap: Map<string, string>;
  /** False when the lookup threw; callers must not treat empty maps as "no products". */
  ok: boolean;
}

/** Same normalizer as PickListPrintable / BulkPickListPrintable. */
export function normalizeWorkerSku(sku: string | null | undefined): string | null {
  if (!sku) return null;
  return sku.toLowerCase().trim().replace(/\s+/g, ' ');
}

/**
 * Exact SKU first, then normalized. Mirrors the cascade used by return-shipment
 * and the print pick sheets so live Shopify SKUs and DB SKUs that only differ
 * by case/whitespace still resolve.
 */
export function resolveWorkerProduct(
  maps: Pick<WorkerLocationMap, 'productMap' | 'productMapByNorm'>,
  sku: string | null | undefined,
): WorkerProductInfo | null {
  if (!sku) return null;
  const exact = maps.productMap.get(sku);
  if (exact) return exact;
  const norm = normalizeWorkerSku(sku);
  if (!norm) return null;
  return maps.productMapByNorm.get(norm) ?? null;
}

export async function loadWorkerProductLocations(
  adminId: string,
  skus: string[]
): Promise<WorkerLocationMap> {
  const productMap = new Map<string, WorkerProductInfo>();
  const productMapByNorm = new Map<string, WorkerProductInfo>();
  const locationMap = new Map<string, string>();

  if (skus.length === 0) {
    return { productMap, productMapByNorm, locationMap, ok: true };
  }

  try {
    const querySkus = Array.from(
      new Set(
        skus.flatMap((sku) => {
          const norm = normalizeWorkerSku(sku);
          return norm && norm !== sku ? [sku, norm] : [sku];
        }),
      ),
    );

    const { data: products, error: productsError } = await supabase
      .from('products')
      .select('id, sku, barcode, image_url, color_id, size_id')
      .eq('admin_id', adminId)
      .in('sku', querySkus);

    if (productsError) throw productsError;

    if (!products || products.length === 0) {
      return { productMap, productMapByNorm, locationMap, ok: true };
    }

    const colorIds = Array.from(
      new Set(products.map((p) => p.color_id).filter((v): v is string => !!v)),
    );
    const sizeIds = Array.from(
      new Set(products.map((p) => p.size_id).filter((v): v is string => !!v)),
    );

    const [colorsRes, sizesRes] = await Promise.all([
      colorIds.length > 0
        ? supabase.from('product_colors').select('id, name').in('id', colorIds)
        : Promise.resolve({ data: [] as { id: string; name: string }[] }),
      sizeIds.length > 0
        ? supabase.from('product_sizes').select('id, name').in('id', sizeIds)
        : Promise.resolve({ data: [] as { id: string; name: string }[] }),
    ]);

    const colorById = new Map((colorsRes.data || []).map((c) => [c.id, c.name]));
    const sizeById = new Map((sizesRes.data || []).map((s) => [s.id, s.name]));

    products.forEach((p) => {
      const info: WorkerProductInfo = {
        id: p.id,
        barcode: p.barcode,
        image_url: p.image_url,
        color_name: p.color_id ? colorById.get(p.color_id) ?? null : null,
        size_name: p.size_id ? sizeById.get(p.size_id) ?? null : null,
      };
      productMap.set(p.sku, info);
      const norm = normalizeWorkerSku(p.sku);
      if (norm && !productMapByNorm.has(norm)) productMapByNorm.set(norm, info);
    });

    const productIds = products.map((p) => p.id);

    const { data: binData, error: binError } = await supabase
      .from('bin_inventory')
      .select(`
        product_id,
        warehouse_bins!inner (
          bin_code,
          level_number,
          warehouse_racks!inner (
            rack_code,
            warehouse_zones (
              zone_code
            )
          )
        )
      `)
      .eq('admin_id', adminId)
      .in('product_id', productIds);

    if (binError) throw binError;

    if (binData) {
      binData.forEach((item: any) => {
        const bin = item.warehouse_bins;
        const rack = bin?.warehouse_racks;
        const zone = rack?.warehouse_zones;

        const zoneCode = zone?.zone_code || 'Z1';
        const rackCode = rack?.rack_code?.replace('RACK-', 'R') || 'R1';
        const level = `L${bin?.level_number || 1}`;

        const shortLocation = `${zoneCode}, ${rackCode}, ${level}`;
        locationMap.set(item.product_id, shortLocation);
      });
    }

    return { productMap, productMapByNorm, locationMap, ok: true };
  } catch (err) {
    console.error('Failed to load worker product locations:', err);
    return { productMap, productMapByNorm, locationMap, ok: false };
  }
}

export function formatBinLocation(
  zoneCode: string | null,
  rackCode: string | null,
  levelNumber: number | null
): string {
  const zone = zoneCode || 'Z1';
  const rack = rackCode?.replace('RACK-', 'R') || 'R1';
  const level = `L${levelNumber || 1}`;
  return `${zone}, ${rack}, ${level}`;
}
